import { useEffect } from 'react';

/**
 * Lightweight head manager for client-rendered (CRA) routes. Sets the document
 * title, meta description, social tags, canonical link, and a JSON-LD block on
 * mount, then restores the previous values on unmount so navigating away (e.g.
 * back to the landing page) doesn't leak a stale title/description.
 *
 * Pass a stable `jsonLd` reference (defined at module scope) to avoid the effect
 * re-running on every render.
 */
const Seo = ({ title, description, canonical, jsonLd }) => {
  useEffect(() => {
    const cleanups = [];

    const applyMeta = (attr, key, value) => {
      if (value == null) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      let created = false;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', value);
      cleanups.push(() => {
        if (created) el.remove();
        else if (prev != null) el.setAttribute('content', prev);
      });
    };

    if (title) {
      const prevTitle = document.title;
      document.title = title;
      cleanups.push(() => {
        document.title = prevTitle;
      });
    }

    applyMeta('name', 'description', description);
    applyMeta('property', 'og:title', title);
    applyMeta('property', 'og:description', description);
    applyMeta('name', 'twitter:title', title);
    applyMeta('name', 'twitter:description', description);

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      let created = false;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
        created = true;
      }
      const prev = link.getAttribute('href');
      link.setAttribute('href', canonical);
      cleanups.push(() => {
        if (created) link.remove();
        else if (prev != null) link.setAttribute('href', prev);
      });
    }

    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', 'route');
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
      cleanups.push(() => script.remove());
    }

    return () => cleanups.forEach((fn) => fn());
  }, [title, description, canonical, jsonLd]);

  return null;
};

export default Seo;
